import { createContext, forwardRef, useContext, useEffect, useMemo, useRef, useState, type AnchorHTMLAttributes, type ReactNode } from "react";
import type { LinkComponentProps } from "@cloudflare/kumo";
import { trackPageView } from "./analytics";

interface RouterValue {
  path: string;
  search: string;
  navigate: (path: string) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

const legacyPaths: Record<string, string> = {
  "/traffic": "/click-events",
  "/sources": "/channels",
  "/acquisition": "/channels",
  "/platform-connections": "/channels",
  "/integrations": "/channels",
  "/tracking-setup": "/sites",
  "/advanced-tracking": "/sites",
};

function canonicalPath(path: string) {
  const pathname = new URL(path, window.location.origin).pathname;
  return legacyPaths[pathname] || pathname;
}

function currentLocation() {
  return { path: canonicalPath(window.location.pathname), search: window.location.search };
}

function canonicalLocation(path: string) {
  const url = new URL(path, window.location.origin);
  return { path: canonicalPath(url.pathname), search: url.search };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState(currentLocation);
  const initialLocation = useRef(location);

  useEffect(() => {
    const update = () => setLocation(currentLocation());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  useEffect(() => {
    const canonical = canonicalPath(window.location.pathname);
    if (window.location.pathname !== canonical) window.history.replaceState({}, "", `${canonical}${window.location.search}`);
  }, [location]);

  useEffect(() => {
    // Firebase automatically records the initial page view at initialization.
    // This app uses client-side routing, so later route changes need explicit events.
    if (location.path !== initialLocation.current.path || location.search !== initialLocation.current.search) {
      trackPageView(`${location.path}${location.search}`);
    }
  }, [location]);

  const value = useMemo(
    () => ({
      path: location.path,
      search: location.search,
      navigate: (nextPath: string) => {
        const nextLocation = canonicalLocation(nextPath);
        if (window.location.pathname === nextLocation.path && window.location.search === nextLocation.search) return;
        window.history.pushState({}, "", `${nextLocation.path}${nextLocation.search}`);
        setLocation(nextLocation);
        window.scrollTo({ top: 0, behavior: "instant" });
      },
    }),
    [location],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const context = useContext(RouterContext);
  if (!context) throw new Error("useRouter must be used within RouterProvider");
  return context;
}

export function AppLink({
  to,
  children,
  className,
  onClick,
  "aria-label": ariaLabel,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  "aria-label"?: string;
}) {
  const { navigate } = useRouter();
  const adminView = new URLSearchParams(window.location.search);
  const adminTenantId = adminView.get("admin_tenant");
  const adminUser = adminView.get("admin_user");
  const activeSiteId = adminView.get("site");
  let destination = adminTenantId && to !== "/admin"
    ? `${to}${to.includes("?") ? "&" : "?"}admin_tenant=${encodeURIComponent(adminTenantId)}${adminUser ? `&admin_user=${encodeURIComponent(adminUser)}` : ""}`
    : to;
  if (activeSiteId && destination.startsWith("/") && !new URL(destination, window.location.origin).searchParams.has("site")) destination += `${destination.includes("?") ? "&" : "?"}site=${encodeURIComponent(activeSiteId)}`;
  return (
    <a
      href={destination}
      className={className}
      aria-label={ariaLabel}
      onClick={(event) => {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        navigate(destination);
        onClick?.();
      }}
    >
      {children}
    </a>
  );
}

/** Bridges Kumo links and navigation components to the console's lightweight router. */
export const KumoRouterLink = forwardRef<HTMLAnchorElement, LinkComponentProps>(
  function KumoRouterLink({ href = "", onClick, ...props }, ref) {
    const { navigate } = useRouter();
    const adminView = new URLSearchParams(window.location.search);
    const adminTenantId = adminView.get("admin_tenant");
    const adminUser = adminView.get("admin_user");
    const activeSiteId = adminView.get("site");
    let destination = adminTenantId && href !== "/admin" && href.startsWith("/")
      ? `${href}${href.includes("?") ? "&" : "?"}admin_tenant=${encodeURIComponent(adminTenantId)}${adminUser ? `&admin_user=${encodeURIComponent(adminUser)}` : ""}`
      : href;
    if (activeSiteId && destination.startsWith("/") && !new URL(destination, window.location.origin).searchParams.has("site")) destination += `${destination.includes("?") ? "&" : "?"}site=${encodeURIComponent(activeSiteId)}`;

    return (
      <a
        {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
        ref={ref}
        href={destination}
        onClick={(event) => {
          onClick?.(event);
          if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            !destination.startsWith("/")
          ) return;
          event.preventDefault();
          navigate(destination);
        }}
      />
    );
  },
);
